"use client";

import { BanIcon, EditIcon, MoreVerticalIcon, PlusIcon } from "lucide-react";
import { useRouter } from "next/navigation";
import React, { PropsWithChildren } from "react";

import { NextTags, Routes } from "@/api";
import { FollowsProvider } from "@/providers/follows-provider";
import { Portfolio, PortfolioSource } from "@/services/portfolios/schema";

import { deletePortfolioAction } from "@/app/actions/portfolio/portfolio-actions";
import { withStopPropagation } from "@/utils/with-stop-propagation";
import { useSession } from "next-auth/react";
import { Button, buttonVariants } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { UserFollow } from "../user/user-follow";
import { RemoveDialog } from "./portfolio/dialogs/remove-dialog";

import { useTranslations } from "next-intl";
import Link from "next/link";
import { PortfolioSyncButton } from "./portfolio/portfolio-sync-button";

interface Props {
  readonly portfolio: Portfolio;
  readonly portfolioSources: PortfolioSource[];
  readonly remainingSyncCount: number;
  readonly affiliateCode?: string;
}

export function PortfolioNav(props: PropsWithChildren<Props>) {
  // const router = useRouter();
  // const [isPending] = useTransition();
  const { data: session } = useSession();

  const portfolioId = props.portfolio.id;
  const portfolioName = props.portfolio.name;
  const portfolioCreatedAt = props.portfolio.createdAt;

  const isUserPortfolio = session?.user?.id === props.portfolio.userId;
  if (!portfolioId || !portfolioName || !portfolioCreatedAt) {
    return null;
  }

  const isManual = props.portfolioSources.every(
    (source) => source.type === "MANUAL",
  );

  return (
    <section className="flex flex-col justify-center gap-1 md:justify-start">
      {/* Follow user */}
      {session?.user.id ? (
        <FollowsProvider
          portfolioId={portfolioId}
          userId={props.portfolio.userId}
        >
          <UserFollow />
        </FollowsProvider>
      ) : null}
      {/* Alert toggle */}
      {/* {!isUserPortfolio && userSubscription.isSubscribed && (
          <Button
            variant={isOnWatchlist ? "secondary" : "outline"}
            className="hover:cursor-pointer hover:bg-secondary"
            onClick={toggleWatchlistClick}
            disabled={
              isOnWatchlistMutation.isPending ||
              isOnWatchlistQuery.isFetching ||
              isPending
            }
          >
            <BellIcon
              className={cn(
                "mr-2 size-4",
                isOnWatchlist ? "fill-primary" : "",
              )}
            />
            {isOnWatchlist ? "Alerting" : "Alert me"}
          </Button>
        )} */}
      {/* Share Portfolio */}
      {/* <PortfolioShare
          portfolioId={portfolioId}
          affiliateCode={props.affiliateCode}
          textHidden={!isDesktop}
        /> */}
      {/* User subscription as children */}
      {props.children}
      <div className="flex grow flex-col">
        <span className="flex flex-1 items-center gap-2">
          {isManual ? (
            <PortfolioNavAddTransactionButton
              portfolio={props.portfolio}
              isUserPortfolio={isUserPortfolio}
            />
          ) : null}
          <PortfolioSyncButton
            portfolioId={portfolioId}
            portfolioName={portfolioName}
            sources={props.portfolioSources}
            remainingSyncCount={props.remainingSyncCount}
          />
          {isUserPortfolio ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:cursor-pointer"
                  gtmEvent={{
                    event: "click",
                    eventCategory: "portfolio",
                    eventLabel: "portfolio-tools",
                    eventAction: "share",
                    userId: session?.user.id,
                    portfolioId: portfolioId,
                  }}
                >
                  <MoreVerticalIcon className="size-4 text-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="flex w-auto flex-col justify-center p-0">
                {/* Edit Portfolio */}
                <PortfolioNavEditButton portfolioId={portfolioId} />
                {/* Remove Portfolio */}
                <PortfolioNavRemoveButton
                  portfolioId={portfolioId}
                  portfolioName={portfolioName}
                />
              </PopoverContent>
            </Popover>
          ) : null}
        </span>
      </div>
      {/* {isDevMode ? (
        <Button
          size="sm"
          variant="outline"
          disabled={isPending || portfolioCopyMutation.isPending}
          onClick={onCopyClick}
        >
          <CopyIcon className="mr-2 size-4" />
          <span className="hidden md:flex">Copy Portfolio</span>
        </Button>
      ) : null} */}
    </section>
  );
}

interface PortfolioNavAddTransactionProps extends Props {
  readonly isUserPortfolio: boolean;
}

function PortfolioNavAddTransactionButton(
  props: Pick<PortfolioNavAddTransactionProps, "portfolio" | "isUserPortfolio">,
) {
  const t = useTranslations("Portfolio.nav");
  if (!props.isUserPortfolio || !props.portfolio?.id) {
    return null;
  }

  return (
    <div className="">
      <Link
        className={buttonVariants({
          size: "sm",
          variant: "outline",
          className: "gap-2",
        })}
        href={Routes.PORTFOLIO_ASSET_ADD(props.portfolio?.id)}
      >
        <>
          <PlusIcon className="size-4" />
          {t("addTransaction")}
        </>
      </Link>
    </div>
  );
}

export function PortfolioNavEditButton(props: {
  portfolioId: string;
  onEdit?: () => void;
}) {
  const { data: session } = useSession();
  const router = useRouter();
  const t = useTranslations("Portfolio.nav");

  const editPortfolioClick = (id?: string) => {
    router.push(Routes.PORTFOLIO_EDIT(id ?? ""));
  };

  const onEditPortfolioClick = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    editPortfolioClick(props.portfolioId);
    props.onEdit?.();
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="justify-start gap-2 hover:cursor-pointer"
      onClick={onEditPortfolioClick}
      gtmEvent={{
        event: "click",
        eventCategory: "portfolio",
        eventLabel: "dashboard-portfolios",
        eventAction: "edit",
        userId: session?.user.id,
        portfolioId: props.portfolioId,
      }}
    >
      <EditIcon className="size-4 text-foreground" />
      <span>{t("edit")}</span>
    </Button>
  );
}
export function PortfolioNavRemoveButton(props: {
  portfolioId: string;
  portfolioName: string;
  onRemove?: () => void;
}) {
  const t = useTranslations("Portfolio.nav.remove");
  const { data: session } = useSession();
  const router = useRouter();

  const removePortfolioClick = React.useCallback(
    (id?: string) => {
      deletePortfolioAction(id ?? "", {
        path: Routes.USER_PORTFOLIOS(session?.user.id ?? ""),
        tag: [
          NextTags.USER_PORTFOLIOS(session?.user.id ?? ""),
          NextTags.USER_PORTFOLIOS(""),
          NextTags.PORTFOLIO(""),
        ],
      }).then(async () => {
        router.replace(Routes.USER_PORTFOLIOS(session?.user.id ?? ""));
        router.refresh();
      });
    },
    [router, session?.user.id],
  );

  const onRemovePortfolioClick = () => {
    props.onRemove?.();
    removePortfolioClick(props.portfolioId);
  };

  return (
    <RemoveDialog
      title={"Remove portfolio"}
      description={t("description", { name: props.portfolioName })}
      cancel={{
        text: t("cancel"),
        onClick: (e) => e.stopPropagation(),
      }}
      confirm={{
        text: t("confirm"),
        onClick: onRemovePortfolioClick,
      }}
    >
      <Button
        variant="ghost"
        size="sm"
        className="justify-start gap-2 hover:bg-destructive hover:text-destructive-foreground"
        onClick={withStopPropagation()}
        gtmEvent={{
          event: "click",
          eventCategory: "portfolio",
          eventLabel: "dashboard-portfolios",
          eventAction: "remove",
          userId: session?.user.id,
          portfolioId: props.portfolioId,
        }}
      >
        <BanIcon className="size-4" />
        <span>{t("label")}</span>
      </Button>
    </RemoveDialog>
  );
}
